import { useEffect, useMemo, useState } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { CAPABILITY_TYPE_LABELS } from '@/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VisibilitySelect, type CapabilityVisibility } from '@/components/ui/visibility-select';
import { getCapabilityColors } from '@/lib/capability-colors';
import { AgentCreateWizard } from './agent-create/AgentCreateWizard';
import { PromptCreateWizard } from './prompt-create/PromptCreateWizard';
import { IntegrationCreateWizard } from './integration-create/IntegrationCreateWizard';
import { ConnectionCreateWizard } from './connection-create/ConnectionCreateWizard';

/** Created capability shape returned by the API (minimal for callers that need it) */
export interface CreatedCapability {
   id?: string;
   name: string;
   description?: string | null;
   [key: string]: unknown;
}

interface CreateCapabilityDrawerProps {
   open: boolean;
   onOpenChange: (open: boolean) => void;
   appKey: string;
   capabilityType: string;
   /** Called with the created entity when creation succeeds (e.g. to add node in workflow) */
   onSuccess?: (entity: CreatedCapability) => void;
}

function formatCapabilityTypeLabel(type: string): string {
   if (CAPABILITY_TYPE_LABELS[type]) {
      return CAPABILITY_TYPE_LABELS[type];
   }

   return type
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function CreateCapabilityDrawer({
   open,
   onOpenChange,
   appKey,
   capabilityType,
   onSuccess,
}: CreateCapabilityDrawerProps) {
   const createCapability = useCapabilityCreate(appKey);
   const [name, setName] = useState('');
   const [key, setKey] = useState('');
   const [description, setDescription] = useState('');
   const [visibility, setVisibility] = useState<CapabilityVisibility>('account');
   const [errors, setErrors] = useState<Record<string, string>>({});

   const typeLabel = useMemo(
      () => formatCapabilityTypeLabel(capabilityType),
      [capabilityType],
   );
   const TypeIcon = getCapabilityColors(capabilityType).icon;

   const slugifyToUnderscore = (value: string): string =>
      value
         .toLowerCase()
         .trim()
         .replace(/[^a-z0-9\s_-]/g, '')
         .replace(/[\s-]+/g, '_')
         .replace(/_+/g, '_')
         .replace(/^_+|_+$/g, '');

   useEffect(() => {
      if (open) {
         setErrors({});
         setName('');
         setKey('');
         setDescription('');
         setVisibility('account');
      }
   }, [open]);

   const handleNameChange = (value: string) => {
      setName(value);
      setKey(slugifyToUnderscore(value));
   };

   const handleCreate = async () => {
      if (!name.trim() || !key.trim()) {
         setErrors({
            ...(!name.trim() ? { name: 'Name is required' } : {}),
            ...(!key.trim() ? { key: 'Key is required' } : {}),
         });
         return;
      }

      try {
         const capabilityName = key.trim();
         const result = await createCapability.mutateAsync({
            name: capabilityName,
            display_name: name.trim() || null,
            type: capabilityType,
            description: description.trim() || null,
            visibility,
         });

         toast.success(`${typeLabel} created.`);
         if (onSuccess && result && typeof result === 'object' && 'name' in result) {
            onSuccess(result as CreatedCapability);
         }
         onOpenChange(false);
      } catch (error) {
         const message =
            error instanceof Error ? error.message : `Unable to create ${typeLabel}.`;
         toast.error(message);
      }
   };

   const inputClassName =
      'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
   const errorInputClassName =
      'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

   return (
      <SlideDrawer isOpen={open} onClose={() => onOpenChange(false)}>
                  {capabilityType === 'agent' ? (
                     <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <AgentCreateWizard
                           appKey={appKey}
                           onClose={() => onOpenChange(false)}
                           onSuccess={onSuccess}
                        />
                     </div>
                  ) : capabilityType === 'integration' ? (
                     <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <IntegrationCreateWizard
                           appKey={appKey}
                           onClose={() => onOpenChange(false)}
                           onSuccess={onSuccess}
                        />
                     </div>
                  ) : capabilityType === 'connection' ? (
                     <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <ConnectionCreateWizard
                           appKey={appKey}
                           onClose={() => onOpenChange(false)}
                           onSuccess={onSuccess}
                        />
                     </div>
                  ) : capabilityType === 'prompt' ? (
                     <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <PromptCreateWizard
                           appKey={appKey}
                           onClose={() => onOpenChange(false)}
                           onSuccess={onSuccess}
                        />
                     </div>
                  ) : (
                     <>
                        <div className="relative border-b border-slate-100 bg-slate-50/50 px-8 py-6">
                           <div className="mb-2 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                 <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
                                    <TypeIcon className="h-5 w-5 text-violet-600" />
                                 </div>
                                 <h2 className="text-2xl font-semibold text-slate-900">
                                    Add New {typeLabel}
                                 </h2>
                              </div>
                              <button
                                 onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onOpenChange(false);
                                 }}
                                 className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 outline-none focus:ring-2 focus:ring-slate-900"
                              >
                                 <X size={20} />
                              </button>
                           </div>
                           <p className="text-sm text-slate-500">
                              Define a new {typeLabel.toLowerCase()} with an initial structure.
                           </p>
                        </div>

                        <div className="flex-1 overflow-y-auto bg-white p-8">
                           <div className="mx-auto max-w-2xl space-y-6">
                              <div className="space-y-2">
                                 <Label>
                                    {typeLabel} Name <span className="text-rose-500">*</span>
                                 </Label>
                                 <Input
                                    placeholder={`e.g. ${typeLabel === 'Workflow' ? 'Document Processing Pipeline' : `${typeLabel} Example`}`}
                                    value={name}
                                    onChange={(event) => handleNameChange(event.target.value)}
                                    className={errors.name ? errorInputClassName : inputClassName}
                                    autoFocus
                                 />
                                 {errors.name && (
                                    <p className="text-xs text-red-500">{errors.name}</p>
                                 )}
                              </div>

                              <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                                 <div className="space-y-2">
                                    <Label>
                                       Key <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                       placeholder={`e.g. ${typeLabel === 'Workflow' ? 'document_processing_pipeline' : `${capabilityType}_example`}`}
                                       value={key}
                                       onChange={(event) => setKey(slugifyToUnderscore(event.target.value))}
                                       className={`font-mono text-xs ${errors.key ? errorInputClassName : inputClassName}`}
                                    />
                                    <p className="text-xs text-slate-400">
                                       Unique identifier for the {typeLabel.toLowerCase()}. Auto-generated from name.
                                    </p>
                                    {errors.key && (
                                       <p className="text-xs text-red-500">{errors.key}</p>
                                    )}
                                 </div>

                                 <VisibilitySelect
                                    value={visibility}
                                    onChange={setVisibility}
                                    className={inputClassName}
                                 />
                              </div>

                              <div className="space-y-2">
                                 <Label>Description</Label>
                                 <textarea
                                    placeholder={`Brief description of what this ${typeLabel.toLowerCase()} does...`}
                                    value={description}
                                    onChange={(event) => setDescription(event.target.value)}
                                    rows={3}
                                    className={`${inputClassName} resize-none`}
                                 />
                              </div>
                           </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-8 py-6">
                           <button
                              onClick={(event) => {
                                 event.preventDefault();
                                 event.stopPropagation();
                                 onOpenChange(false);
                              }}
                              disabled={createCapability.isPending}
                              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg disabled:opacity-50"
                           >
                              Cancel
                           </button>
                           <button
                              onClick={(event) => {
                                 event.preventDefault();
                                 event.stopPropagation();
                                 void handleCreate();
                              }}
                              disabled={createCapability.isPending || !name.trim() || !key.trim()}
                              className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-btn-primary-hover hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                           >
                              {createCapability.isPending ? (
                                 <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating...
                                 </>
                              ) : (
                                 `Create ${typeLabel}`
                              )}
                           </button>
                        </div>
                     </>
                  )}
      </SlideDrawer>
   );
}

export default CreateCapabilityDrawer;
